import { Box, Button, CircularProgress, FormControl, IconButton, MenuItem, Select } from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import GridLayout from "react-grid-layout";
import { getPermissionData, getUserData } from "../../Actions/common";
import {
  addCustomDashboardLayoutAction,
  deleteCustomDashboardLayoutAction,
  getCustomDashboardDataAction,
  updateCustomDashboardLayoutAction,
  updateDashboardLayoutSelectionAction,
} from "../../Actions/DashboardActions";
import { getTenantAction } from "../../Actions/TenantActions";
import { listAllCommonTenants } from "../../Actions/PaycentralAction";
import { getAllChartsAndCardDetailsAction } from "../../Actions/TransactionActions";
import CustomSnackbar from "../Common/snackBar";
import DateRangePicker from "../Common/DateRangePicker";
import "./Dashboard.css";
import "./styles.css";
import "/node_modules/react-grid-layout/css/styles.css";
import "/node_modules/react-resizable/css/styles.css";
import { MyRestartAltRoundedIcon, MySettingsIcon } from "../../Assets/svg/MyIcons";
import DashboardStatsCards from "./DashboardStatsCards";
import DashboardTransactionGraph from "./DashboardTransactionGraph";
import DashboardActivityGraph from "./DashboardActivityGraph";
import DashboardRevenueGraph from "./DashboardRevenueGraph";
import DashboardPaymentDistributionGraph from "./DashboardPaymentDistributionGraph";
import DashboardRecentTransactions from "./DashboardRecentTransactions";
import CorporationFeeGraph from "./PaycentralFeeGraph"
import { DashboardLayoutSidebar, DashboardLayoutSidebarForCustomise } from "./DashboardLayoutSidebars";
import DashboardTopTenants from "./DashboardTopTenants";
import { TIME_RANGES, getDefaultDateRange } from "./DashboardUtils";
import showConfirmation from "../../utils/Conformation";
import { getCorporationFeeKPIAction } from "../../Actions/PaycentralAction";

// Default layout configuration - moved outside component for better performance
const DEFAULT_LAYOUT_CONFIG = [
  // 🔹 ROW 1 (4 cards)
  { i: "totalTransactionAmount", x: 0, y: 0, w: 3, h: 4 },
  { i: "totalOnlineTransaction", x: 3, y: 0, w: 3, h: 4 },
  { i: "totalOfflineTransaction", x: 6, y: 0, w: 3, h: 4 },
  { i: "totalSuccessfulTransactions", x: 9, y: 0, w: 3, h: 4 },

  // 🔹 ROW 2 (4 cards)
  { i: "totalPendingTransactions", x: 0, y: 4, w: 3, h: 4 },
  { i: "totalFailedTransactions", x: 3, y: 4, w: 3, h: 4 },
  { i: "totalRefundTransactions", x: 6, y: 4, w: 3, h: 4 },

  // // 👉 ADD FEES HERE (same section)
  // { i: "oneTimeSetupFee", x: 9, y: 4, w: 3, h: 4 },

  // // 🔹 ROW 3 (ALL FEES CONTINUE)
  // { i: "ongoingMaintenanceFee", x: 0, y: 8, w: 3, h: 4 },
  // { i: "newMerchantSetupFee", x: 3, y: 8, w: 3, h: 4 },
  // { i: "ongoingMaintenancePerMerchantFee", x: 6, y: 8, w: 3, h: 4 },

  // (optional empty space or future card)
  // { i: "extraCard", x: 9, y: 8, w: 3, h: 4 },

  // 🔻 NOW CHARTS START AFTER ALL CARDS
  { i: "totalTransactionCountChart", x: 0, y: 12, w: 6, h: 12 },
  { i: "transactionActivityChart", x: 6, y: 12, w: 6, h: 12 },

  { i: "revenueMerchantChart", x: 0, y: 24, w: 8, h: 12 },
  { i: "paymentDistributionChart", x: 8, y: 24, w: 4, h: 12 },

  // 🔥 CORPORATION FEES (MOVED UP + RESIZED)
  { i: "corporationFeeChart", x: 0, y: 36, w: 12, h: 12 },

  // 📄 RECENT TRANSACTIONS (NOW LAST)
  { i: "recentTransactionList", x: 0, y: 48, w: 12, h: 8 },
  // { i: "topPerformingMerchantList", x: 0, y: 46, w: 12, h: 20 },
];

// Helper function to update layout config properties
const updateLayoutConfig = (items, { static: isStatic, isResizable }) =>
  items.map((item) => ({
    ...item,
    static: isStatic,
    isResizable
  }));

const DashboardSection = () => {
  // Global filter state
  const [selectedTimeRange, setSelectedTimeRange] = useState("six months");
  const [selectedDateRange, setSelectedDateRange] = useState(getDefaultDateRange());
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [selectedWebsite, setSelectedWebsite] = useState("All Websites");
  const [enableFilterButton, setEnableFilterButton] = useState(false);

  // Data state
  const [merchantData, setMerchantData] = useState([]);
  const [merchantOptions, setMerchantOptions] = useState(["All Merchant"]);
  const [globalKpiData, setGlobalKpiData] = useState({
    totalTransactionAmount: { amount: "0.00", count: "0" },
    totalSuccessfulTransactions: { amount: "0", count: "0" },
    totalPendingTransactions: { amount: "0 ", count: "0" },
    totalRefundTransactions: { amount: "0", count: "0" },
    totalFailedTransactions: { amount: "0.00", count: "0" },
    totalOnlineTransaction: { amount: "0.00", count: "0" },
    totalOfflineTransaction: { amount: "0.00", count: "0" },

    oneTimeSetupFee: "1000",
    ongoingMaintenanceFee: "450",
    newMerchantSetupFee: "110",
    ongoingMaintenancePerMerchantFee: "450",
  });
  const [visibleCardTypes, setVisibleCardTypes] = useState({
    showTransactionCards: true,
    showFeeCards: true,
  });
  const [kpiDataLoaded, setKpiDataLoaded] = useState(false);

  // Layout state
  const [getDataForDashboard, setDataForDashboard] = useState([]);
  const [layoutLoading, setLayoutLoading] = useState(true);
  const [layoutSlideIsOpen, setLayoutSlideIsOpen] = useState(false);
  const [layoutSlideUpdateIsOpen, setLayoutSlideUpdateIsOpen] = useState(false);
  const [layout, setLayout] = useState("default");
  const [selectedLayout, setSelectedLayout] = useState("");
  const [showButton, setShowButton] = useState(false);
  const [floatMenu, setFloatMenu] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isManualLayoutChange, setIsManualLayoutChange] = useState(false);
  const [isLayoutSelected, setIsLayoutSelected] = useState(false);
  const [selectedPortal, setSelectedPortal] = useState("all");
  const containerRef = useRef(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  //get tenant slection funciton 
  const getTenantType = () => {
    if (selectedPortal === "all") return "all";
    if (selectedPortal === "paycentral") return "pid";
    if (selectedPortal === "qbot") return "qid";
    if (selectedPortal === "tripola") return "tid";
    if (selectedPortal === "kyc") return "kid";

    return "all";
  };

  const PORTAL_OPTIONS = [
    { label: "All Portal", value: "all" },
    { label: "Tripola", value: "tripola" },
    { label: "KYC", value: "kyc" },
    { label: "Paycentral", value: "paycentral" },
    { label: "QBot", value: "qbot" },
  ];


  const handlePortalChange = (event) => {
    const value = event.target.value;

    setSelectedPortal(value === "all" ? "all" : value);
    setEnableFilterButton(true);
  };


  const getVisibleCardTypes = useCallback((merchant) => {
    if (!merchant) {
      return { showTransactionCards: true, showFeeCards: true }; // All Tenants
    }

    const hasP = !!merchant.pId;
    const hasQ = !!merchant.qId;

    if (hasP && hasQ) {
      return { showTransactionCards: true, showFeeCards: true };
    }
    if (hasP) {
      return { showTransactionCards: false, showFeeCards: true };   // Only PID → Fees only
    }
    if (hasQ) {
      return { showTransactionCards: true, showFeeCards: false };   // Only QID → Transactions only
    }

    return { showTransactionCards: true, showFeeCards: true };
  }, []);
  // Memoize defaultLayout to avoid recreating on every render
  const defaultLayout = useMemo(
    () => updateLayoutConfig(DEFAULT_LAYOUT_CONFIG, { static: false, isResizable: true }),
    []
  );



  // Initialize getValues with static layout - ensure components are NOT draggable by default
  const [getValues, setGetValues] = useState(() =>
    updateLayoutConfig(DEFAULT_LAYOUT_CONFIG, { static: true, isResizable: false })
  );


  // 🔥 FINAL FIXED Dynamic Layout
  const dynamicLayout = useMemo(() => {
    const transactionCardKeys = [
      "totalTransactionAmount",
      "totalOnlineTransaction",
      "totalOfflineTransaction",
      "totalSuccessfulTransactions",
      "totalPendingTransactions",
      "totalFailedTransactions",
      "totalRefundTransactions",
    ];

    const feeCardKeys = [
      "oneTimeSetupFee",
      "ongoingMaintenanceFee",
      "newMerchantSetupFee",
      "ongoingMaintenancePerMerchantFee",
    ];

    // Step 1: Filter cards based on merchant type
    const visibleItems = getValues.filter((item) => {
      if (transactionCardKeys.includes(item.i)) {
        return visibleCardTypes.showTransactionCards;
      }
      if (feeCardKeys.includes(item.i)) {
        return visibleCardTypes.showFeeCards;
      }
      return true; // charts, recent transactions, top tenants always visible
    });

    // Step 2: Re-position KPI cards compactly from the top (maintains order)
    let currentY = 0;
    const cardHeight = 4;

    const kpiCards = visibleItems.filter(item =>
      transactionCardKeys.includes(item.i) || feeCardKeys.includes(item.i)
    );

    const repositionedKpi = kpiCards.map((item, index) => {
      const newItem = { ...item };
      newItem.y = currentY;
      newItem.x = (index % 4) * 3;     // 4 cards per row
      newItem.w = 3;
      newItem.h = 4;

      if (index % 4 === 3) {           // every 4th card → move to next row
        currentY += cardHeight;
      }
      return newItem;
    });

    // Step 3: Shift charts & tables upward (remove gap)
    const nonKpiItems = visibleItems.filter(item =>
      !transactionCardKeys.includes(item.i) && !feeCardKeys.includes(item.i)
    );

    const shiftedNonKpi = nonKpiItems.map(item => {
      const newItem = { ...item };
      const removedHeight = (3 - Math.ceil(kpiCards.length / 4)) * cardHeight; // 3 original KPI rows
      newItem.y = Math.max(0, item.y - removedHeight);
      return newItem;
    });

    return [...repositionedKpi, ...shiftedNonKpi];
  }, [getValues, visibleCardTypes]);

  //  State to trigger permission recalculation
  const [permissionTrigger, setPermissionTrigger] = useState(0);

  // Permissions - memoized to avoid recalculating
  const { hasTransactionPermission, hasWithdrawalPermission } = useMemo(() => {
    void permissionTrigger; // Reference to satisfy linter

    const user = localStorage.getItem("userData") ? getUserData() : null;
    const permissions = localStorage.getItem(`${user?.id}_permissionData`)
      ? getPermissionData(`${user?.id}_permissionData`)
      : [];

    return {
      hasTransactionPermission: permissions[0]?.role_permissions?.find(
        (m) => m?.module && m?.module?.slug === "transaction"
      ),
      hasWithdrawalPermission: permissions[0]?.role_permissions?.find(
        (m) => m?.module && m?.module?.slug === "withdrawal"
      ),
    };
  }, [permissionTrigger]);

  useEffect(() => {
    const checkPermissions = () => {
      const user = localStorage.getItem("userData") ? getUserData() : null;
      if (!user) return false;

      const permData = localStorage.getItem(`${user?.id}_permissionData`);
      return !!permData;
    };

    if (!checkPermissions()) {
      const maxRetries = 50;
      let retryCount = 0;

      const retryInterval = setInterval(() => {
        retryCount++;
        if (checkPermissions()) {
          setPermissionTrigger(prev => prev + 1);
          clearInterval(retryInterval);
        } else if (retryCount >= maxRetries) {
          clearInterval(retryInterval);
        }
      }, 100);

      return () => clearInterval(retryInterval);
    }
  }, []);


  // Sync visible cards when merchant changes
  useEffect(() => {
    const visibility = getVisibleCardTypes(selectedMerchant);
    setVisibleCardTypes(visibility);
  }, [selectedMerchant, getVisibleCardTypes]);



  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const handleShowSnackbar = useCallback((message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  const handleRemove = useCallback((elemKey) => {
    setGetValues((prevValues) => prevValues.filter((item) => item.i !== elemKey));
  }, []);

  const handleFloatMenu = useCallback(() => {
    // If in edit mode, open customize sidebar directly
    if (isEditMode) {
      setLayoutSlideUpdateIsOpen(true);
    } else {
      // Normal behavior - toggle float menu
      setFloatMenu((prev) => !prev);
    }
  }, [isEditMode]);

  const calculateDateRange = useCallback((timeRange) => {
    if (timeRange === "custom") {
      return { newStartDate: startDate, newEndDate: endDate };
    }

    const now = dayjs();
    let newStartDate, newEndDate;

    switch (timeRange) {
      case "today":
        newStartDate = now.startOf("day");
        newEndDate = now.endOf("day");
        break;
      case "yesterday":
        newStartDate = now.subtract(1, "day").startOf("day");
        newEndDate = now.subtract(1, "day").endOf("day");
        break;
      case "this week":
        newStartDate = now.subtract(6, "day").startOf("day");
        newEndDate = now.endOf("day");
        break;
      case "last 15 days":
        newStartDate = now.subtract(14, "day").startOf("day");
        newEndDate = now.endOf("day");
        break;
      case "one month":
        newStartDate = now.subtract(1, "month").startOf("day");
        newEndDate = now.endOf("day");
        break;
      case "three months":
        newStartDate = now.subtract(3, "month").startOf("day");
        newEndDate = now.endOf("day");
        break;
      case "six months":
        newStartDate = now.subtract(6, "month").startOf("day");
        newEndDate = now.endOf("day");
        break;
      default:
        newStartDate = now.startOf("day");
        newEndDate = now.endOf("day");
    }

    return {
      newStartDate: newStartDate.toISOString(),
      newEndDate: newEndDate.toISOString(),
    };
  }, [startDate, endDate]);


  const filterTenantsByPortal = (tenants, portal) => {
    if (!portal || portal === "all") return tenants;

    return tenants.filter((t) => {
      switch (portal) {
        case "paycentral":
          return !!t.pId;
        case "kyc":
          return !!t.kId;
        case "qbot":
          return !!t.qId;
        case "tripola":
          return !!t.tId;
        default:
          return true;
      }
    });
  };

  const fetchAllMerchantData = useCallback(async () => {
    try {
      const response = await listAllCommonTenants(null, "", null, null, "1");
      const rawData = response?.data?.data || [];

      const filtered = filterTenantsByPortal(rawData, selectedPortal);

      // console.log("Selected Portal:", selectedPortal);   // ✅ HERE
      // console.log("Raw Data:", rawData);                 // ✅ ADD THIS
      // console.log("Filtered Tenants:", filtered);        // ✅ HERE

      if (Array.isArray(response?.data?.data)) {
        const merchantsWithWebsites = filtered.map((merchant) => ({
          ...merchant,
          websites: merchant.websites || [],
        }));

        setMerchantData(filtered);

        const options = ["All Merchant"];
        merchantsWithWebsites.forEach((merchant) => {
          options.push(
            `${merchant.firstName || ""} ${merchant.lastName || ""}`.trim()
          );
        });

        setMerchantOptions(options);

        return merchantsWithWebsites; // ✅ return should be LAST
      } else {
        throw new Error("Invalid merchant data format");
      }
    } catch (error) {
      console.error("Error fetching merchant data:", error);
      handleShowSnackbar("Error fetching merchant data", "error");
      throw error;
    }
  }, [handleShowSnackbar, selectedPortal]);


  const fetchGlobalKpiData = useCallback(async () => {
    try {
      const { newStartDate, newEndDate } = calculateDateRange(selectedTimeRange);

      const response = await getAllChartsAndCardDetailsAction({
        startDate: newStartDate,
        endDate: newEndDate,
        merchant: selectedMerchant,
        websiteId: selectedWebsite || null,
      });

      if (response?.data?.dashboard) {
        const dashboardData = response.data.dashboard;
        setGlobalKpiData({
          totalTransactionAmount: {
            amount: `${Number(dashboardData.totalTransactionAmount || 0).toFixed(2)}`,
            count: `${dashboardData.totalTransactionCount || 0}`,
          },
          totalSuccessfulTransactions: {
            amount: `${dashboardData.totalSuccessTransactionAmount || 0}`,
            count: `${dashboardData.totalSuccessTransactionCount || 0}`,
          },
          totalPendingTransactions: {
            amount: `${dashboardData.totalPendingTransactionAmount || 0}`,
            count: `${dashboardData.totalPendingTransactionCount || 0}`,
          },

          totalRefundTransactions: {
            amount: `${dashboardData.totalRefundAmount || 0}`,
            count: `${dashboardData.totalRefundCount || 0}`,
          },
          totalFailedTransactions: {
            amount: `${Number(dashboardData.totalFailedTransactionAmount || 0).toFixed(2)}`,
            count: `${dashboardData.totalFailedTransactionCount || 0}`,
          },
          totalOnlineTransaction: {
            amount: `${Number(dashboardData.totalOnlinePaymentsAmount || 0).toFixed(2)}`,
            count: `${dashboardData.totalOnlinePaymentsCount || 0}`,
          },
          totalOfflineTransaction: {
            amount: `${Number(dashboardData.totalOfflinePaymentsAmount || 0).toFixed(2)}`,
            count: `${dashboardData.totalOfflinePaymentsCount || 0}`,
          },
        });
        setKpiDataLoaded(true);
      }
    } catch (error) {
      setGlobalKpiData({
        totalTransactionAmount: { amount: "0.00", count: "0" },
        totalSuccessfulTransactions: { amount: "0", count: "0" },
        totalPendingTransactions: { amount: "0", count: "0" },
        totalRefundTransactions: { amount: "0", count: "0" },
        totalFailedTransactions: { amount: "0.00", count: "0" },
        totalOnlineTransaction: { amount: "0.00", count: "0" },
        totalOfflineTransaction: { amount: "0.00", count: "0" },
      });
      setKpiDataLoaded(true);
      if (error?.response?.data?.message) {
        handleShowSnackbar(error.response.data.message, "error");
      }
    }
  }, [selectedTimeRange, selectedMerchant, selectedWebsite, calculateDateRange, handleShowSnackbar]);


  const handleGlobalTimeRangeChange = (event) => {
    const value = event.target.value;
    setSelectedTimeRange(value);

    if (value !== "custom") {
      const { newStartDate, newEndDate } = calculateDateRange(value);
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      setSelectedDateRange({ from: new Date(newStartDate), to: new Date(newEndDate) });
    }

    setEnableFilterButton(true);
  };

  const handleMerchantChange = useCallback((event) => {
    const merchantName = event.target.value;

    if (merchantName === "All Merchant") {
      setSelectedMerchant(null);
      setVisibleCardTypes({ showTransactionCards: true, showFeeCards: true });
      return;
    }

    const merchantObj = merchantData.find(
      (m) =>
        `${m.firstName || ""} ${m.lastName || ""}`.trim() === merchantName
    );

    setSelectedMerchant(merchantObj || null);

    // Update visible cards based on merchant type
    if (merchantObj) {
      const visibility = getVisibleCardTypes(merchantObj);
      setVisibleCardTypes(visibility);
    }

    setSelectedWebsite("All Websites");
    setEnableFilterButton(true);
  }, [merchantData, getVisibleCardTypes]);

  const selectCustomDate = (range) => {
    if (!range || !range.from || !range.to) {
      return;
    }

    const apiStartDate = new Date(range.from);
    apiStartDate.setUTCHours(0, 0, 0, 0);
    const apiEndDate = new Date(range.to);
    apiEndDate.setUTCHours(23, 59, 59, 999);

    const startDateISO = apiStartDate.toISOString();
    const endDateISO = apiEndDate.toISOString();

    const displayStartDate = new Date(range.from);
    displayStartDate.setHours(0, 0, 0, 0);
    const displayEndDate = new Date(range.to);
    displayEndDate.setHours(0, 0, 0, 0);

    setSelectedDateRange({ from: displayStartDate, to: displayEndDate });
    setStartDate(startDateISO);
    setEndDate(endDateISO);
    setSelectedTimeRange("custom");
    setEnableFilterButton(true);
  };

  const handleResetFilters = async () => {
    const defaultRange = getDefaultDateRange();

    setSelectedTimeRange("six months");
    setSelectedDateRange(defaultRange);

    // Correct way to set ISO strings
    setStartDate(defaultRange.from.toISOString());
    setEndDate(defaultRange.to.toISOString());

    // Reset merchant to All Tenants
    setSelectedMerchant(null);
    setSelectedWebsite("All Websites");
    setSelectedPortal("all")
    setEnableFilterButton(false);


    // Force show ALL cards when resetting
    setVisibleCardTypes({
      showTransactionCards: true,
      showFeeCards: true,
    });
  };

  const getCustomLayoutDashboardData = useCallback(async () => {
    try {
      const res = await getCustomDashboardDataAction();
      const layoutData = res?.data?.data?.rows || [];
      setDataForDashboard(layoutData);

      // If no layouts exist, set default and stop loading
      if (layoutData.length === 0) {
        setLayout("default");
        setSelectedLayout("");
        setIsLayoutSelected(true); // Default is considered selected
        const staticLayout = updateLayoutConfig(defaultLayout, { static: true, isResizable: false });
        setGetValues(staticLayout);
        setLayoutLoading(false);
      }
      // If layouts exist, let the useEffect handle the selection
      return layoutData;

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      handleShowSnackbar(error?.response?.data?.message, "error");
      setDataForDashboard([]);
      // On error, fall back to default
      setLayout("default");
      setSelectedLayout("");
      setIsLayoutSelected(true); // Default is considered selected
      const staticLayout = updateLayoutConfig(defaultLayout, { static: true, isResizable: false });
      setGetValues(staticLayout);
      setLayoutLoading(false);
      return [];
    }
  }, [handleShowSnackbar, defaultLayout]);

  const handleUpdateLayoutSelection = useCallback(async (id) => {
    // Handle default layout selection
    const layoutId = id === "default" ? "default" : id;

    await updateDashboardLayoutSelectionAction(layoutId)
      .then(async (res) => { })
      .catch((error) => {
        console.error("Error updating layout selection:", error);
        handleShowSnackbar(error?.response?.data?.message || "Failed to update layout selection", "error");
      });
  }, [handleShowSnackbar]);

  const handleLayoutChange = useCallback(async (event) => {
    const selectedValue = event.target.value;

    // Set manual change flag to prevent auto-reset
    setIsManualLayoutChange(true);

    // Reset layout selection state when changing layout
    setIsLayoutSelected(false);

    // If edit sidebar is open, close it and exit edit mode
    if (layoutSlideUpdateIsOpen) {
      setLayoutSlideUpdateIsOpen(false);
      setIsEditMode(false);
    }

    if (selectedValue === "create-layout") {
      // Exit edit mode when creating new layout
      setIsEditMode(false);
      setLayoutSlideIsOpen(true);
      // In customization mode, components should NOT be resizable
      const staticLayout = updateLayoutConfig(defaultLayout, { static: true, isResizable: false });
      setGetValues(staticLayout);
    } else {
      // Exit edit mode when switching layouts
      setIsEditMode(false);
      const selectedItem = getDataForDashboard?.find((item) => item.id === selectedValue);
      let layoutValue = selectedItem?.layoutsConfig
        ? JSON.parse(selectedItem.layoutsConfig)
        : DEFAULT_LAYOUT_CONFIG;

      // Ensure components are static (not draggable) when previewing
      layoutValue = updateLayoutConfig(layoutValue, { static: true, isResizable: false });
      setGetValues(layoutValue);

      // Check if this layout is already selected in database
      const isCurrentlySelected = selectedItem?.isSelected || (selectedValue === "default" && !getDataForDashboard.some(item => item.isSelected));
      setIsLayoutSelected(isCurrentlySelected);

      // DON'T save to database immediately - just preview the layout
    }
    setLayout(selectedValue);

    // Reset manual change flag after a short delay
    setTimeout(() => {
      setIsManualLayoutChange(false);
    }, 2000);
  }, [defaultLayout, getDataForDashboard, layoutSlideUpdateIsOpen]);

  const handleCancelLayoutUpdate = useCallback((layoutElement) => {
    const layoutsConfigData = JSON.parse(layoutElement?.layoutsConfig || "[]");
    const updatedConfig = updateLayoutConfig(layoutsConfigData, { static: true, isResizable: false });
    setGetValues(updatedConfig);
    setLayoutSlideUpdateIsOpen(false);

    // Exit edit mode and reopen float menu (select layout popup)
    setIsEditMode(false);
    setFloatMenu(true);
  }, []);

  const handleAddLayout = useCallback(async (data) => {
    await addCustomDashboardLayoutAction(data)
      .then(async (res) => {
        await getCustomLayoutDashboardData();
        setLayoutSlideIsOpen(false);
        setLayout(res?.data?.data.layoutName ?? "default");
        handleShowSnackbar(res.data.message, "success");
      })
      .catch((error) => {
        handleShowSnackbar(error?.response?.data?.message, "error");
      });
  }, [getCustomLayoutDashboardData, handleShowSnackbar]);

  const handleUpdateLayout = useCallback(async (data, id) => {
    await updateCustomDashboardLayoutAction(data, id)
      .then(async (res) => {
        await getCustomLayoutDashboardData();
        setLayoutSlideUpdateIsOpen(false);

        // Exit edit mode after successful update and reopen float menu
        setIsEditMode(false);
        setFloatMenu(true);

        handleShowSnackbar(res?.data?.message, "success");
      })
      .catch((error) => {
        handleShowSnackbar(error?.response?.data?.message, "error");
      });
  }, [getCustomLayoutDashboardData, handleShowSnackbar]);

  const handleDeleteLayout = useCallback(async (data) => {
    await showConfirmation("simpleDelete", {
      entityName: "Layout",
      entityId: data,
      customText: "You will not be able to recover this Layout!",
      action: async (id) => {
        return await deleteCustomDashboardLayoutAction(id);
      },
      onSuccess: async () => {
        await getCustomLayoutDashboardData();
        setLayoutSlideUpdateIsOpen(false);
        setLayout("default");
        setGetValues(defaultLayout);
      },
      onError: (error) => {
        handleShowSnackbar(error?.response?.data?.message, "error");
      },
      showCancelledMsg: true,
    });
  }, [defaultLayout, getCustomLayoutDashboardData, handleShowSnackbar]);

  const handleCustomizebtn = useCallback(() => {
    // Close create layout sidebar but keep float menu open for backdrop
    setLayoutSlideIsOpen(false);

    // DON'T enter edit mode - just open customize sidebar for component selection
    // Components should remain static (not draggable) during customization
    const layoutConfig = updateLayoutConfig(getValues, { static: true, isResizable: false });
    setGetValues(layoutConfig);
    setLayoutSlideUpdateIsOpen(true);
  }, [getValues]);

  const handleLayoutSelectionChange = useCallback(async () => {
    try {
      await handleUpdateLayoutSelection(layout);

      // Add a small delay to ensure database is updated before fetching
      await new Promise(resolve => setTimeout(resolve, 500));

      await getCustomLayoutDashboardData();
      if (layout === "default") {
        setSelectedLayout("");
      } else {
        setSelectedLayout(layout);
      }

      // Mark this layout as selected (saved in database)
      setIsLayoutSelected(true);

      // Close the float menu after successful selection
      setFloatMenu(false);

      // Show success message
      handleShowSnackbar("Layout selected successfully!", "success");
    } catch (error) {
      console.error("Error updating layout selection:", error);
      handleShowSnackbar("Failed to update layout selection", "error");
    }
  }, [layout, handleUpdateLayoutSelection, getCustomLayoutDashboardData, handleShowSnackbar]);

  useEffect(() => {
    if (!containerRef.current) return;
    let resizeTimeout;
    const resizeObserver = new ResizeObserver((entries) => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Layout will auto-adjust based on container width
      }, 40);
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  useEffect(() => {
    // Only auto-select layout on initial load, not on every data change
    // Also prevent auto-selection during manual layout changes
    if (layoutLoading && !isManualLayoutChange) {
      if (getDataForDashboard.length > 0) {
        let foundSelectedLayout = false;

        getDataForDashboard.forEach((elem) => {
          if (elem.isSelected) {
            foundSelectedLayout = true;
            setSelectedLayout(elem.id);
            setIsLayoutSelected(true); // This layout is already selected in database
            const layoutConfig = elem.layoutsConfig;
            if (layoutConfig) {
              try {
                const parsedLayout = JSON.parse(layoutConfig);
                // Ensure components are static (not draggable) by default
                const staticLayout = updateLayoutConfig(parsedLayout, { static: true, isResizable: false });
                setGetValues(staticLayout);
              } catch (error) {
                console.error("Error parsing layout config:", error);
                const staticLayout = updateLayoutConfig(defaultLayout, { static: true, isResizable: false });
                setGetValues(staticLayout);
              }
            } else {
              const staticLayout = updateLayoutConfig(defaultLayout, { static: true, isResizable: false });
              setGetValues(staticLayout);
            }
            setLayout(elem.id);
          }
        });

        // If no custom layout is selected, use default
        if (!foundSelectedLayout) {
          setLayout("default");
          setSelectedLayout("");
          setIsLayoutSelected(true); // Default is considered "selected"
          const staticLayout = updateLayoutConfig(defaultLayout, { static: true, isResizable: false });
          setGetValues(staticLayout);
        }

        // Data has been processed, stop loading
        setLayoutLoading(false);
      }
      // Don't set layoutLoading to false if data is empty - wait for API call to complete
    }
  }, [getDataForDashboard, layoutLoading, isManualLayoutChange, defaultLayout]);

  useEffect(() => {
    const initializeDashboard = async () => {
      try {
        // First initialize dates
        const defaultRange = getDefaultDateRange();
        setSelectedDateRange(defaultRange);
        const startDateISO = defaultRange.from.toISOString();
        const endDateISO = defaultRange.to.toISOString();
        setStartDate(startDateISO);
        setEndDate(endDateISO);

        const [, layoutData] = await Promise.all([
          fetchAllMerchantData(),
          getCustomLayoutDashboardData()
        ]);



        //If no custom layouts, ensure loading is stopped
        if (!layoutData || layoutData.length === 0) {
          setLayoutLoading(false);
        }

      } catch (error) {
        console.error("❌ Error initializing dashboard:", error);
        // Ensure loading stops even on error
        setLayoutLoading(false);
        setKpiDataLoaded(true);
      }
    };

    initializeDashboard();
  }, [fetchAllMerchantData, getCustomLayoutDashboardData]); // Empty dependency array for initial load only

  useEffect(() => {
    // Only fetch KPI data on filter changes, not during initial load
    // Initial load is handled in the initialization useEffect
    if (selectedTimeRange && startDate && endDate && !layoutLoading) {
      fetchGlobalKpiData();
    }
  }, [selectedTimeRange, selectedMerchant, selectedWebsite, startDate, endDate, fetchGlobalKpiData, layoutLoading]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 991) {
        setShowButton(false);
      } else {
        setShowButton(true);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Render the components - memoized to avoid recreating on every render
  const renderComponent = useCallback((itemKey) => {
    const tenantType = getTenantType();
    const commonChartProps = {
      selectedMerchant,
      selectedWebsite,
      selectedTimeRange,
      startDate,
      endDate,
      layoutSlideUpdateIsOpen,
      showButton: isEditMode, // Show remove button only in edit mode
      onRemove: handleRemove,
      onShowSnackbar: handleShowSnackbar,
      tenantType
    };

    const statsCardsProps = {
      ...commonChartProps,
      globalKpiData,
      hasTransactionPermission,
      hasWithdrawalPermission,
    };

    const tableProps = {
      showButton: isEditMode, // Show remove button only in edit mode
      onRemove: handleRemove,
      hasTransactionPermission,
      onShowSnackbar: handleShowSnackbar,
    };

    switch (itemKey) {
      case "totalTransactionAmount":
        return (
          <DashboardStatsCards
            key="all-stats-cards"
            cardType="totalTransactionAmount"
            {...statsCardsProps}
          />
        );

      case "totalSuccessfulTransactions":
        return (
          <DashboardStatsCards
            key="totalSuccessfulTransactions-card"
            cardType="totalSuccessfulTransactions"
            {...statsCardsProps}
          />
        );

      case "totalPendingTransactions":
        return (
          <DashboardStatsCards
            key="totalPendingTransactions-card"
            cardType="totalPendingTransactions"
            {...statsCardsProps}
          />
        );

      case "totalRefundTransactions":
        return (
          <DashboardStatsCards
            key="totalRefundTransactions-card"
            cardType="totalRefundTransactions"
            {...statsCardsProps}
          />
        );

      case "oneTimeSetupFee":
        return (
          <DashboardStatsCards
            key="oneTimeSetupFee"
            cardType="oneTimeSetupFee"
            {...statsCardsProps}
          />
        );

      case "ongoingMaintenanceFee":
        return (
          <DashboardStatsCards
            key="ongoingMaintenanceFee"
            cardType="ongoingMaintenanceFee"
            {...statsCardsProps}
          />
        );

      case "newMerchantSetupFee":
        return (
          <DashboardStatsCards
            key="newMerchantSetupFee"
            cardType="newMerchantSetupFee"
            {...statsCardsProps}
          />
        );

      case "ongoingMaintenancePerMerchantFee":
        return (
          <DashboardStatsCards
            key="ongoingMaintenancePerMerchantFee"
            cardType="ongoingMaintenancePerMerchantFee"
            {...statsCardsProps}
          />
        );

      case "totalFailedTransactions":
        return (
          <DashboardStatsCards
            key="totalFailedTransactions-card"
            cardType="totalFailedTransactions"
            {...statsCardsProps}
          />
        );

      case "totalOnlineTransaction":
        return (
          <DashboardStatsCards
            key="totalOnlineTransaction-card"
            cardType="totalOnlineTransaction"
            {...statsCardsProps}
          />
        );

      case "totalOfflineTransaction":
        return (
          <DashboardStatsCards
            key="totalOfflineTransaction-card"
            cardType="totalOfflineTransaction"
            {...statsCardsProps}
          />
        );

      case "totalTransactionCountChart":
        return <DashboardTransactionGraph key={itemKey} {...commonChartProps} />;

      case "transactionActivityChart":
        return <DashboardActivityGraph key={itemKey} {...commonChartProps} />;

      case "revenueMerchantChart":
        return <DashboardRevenueGraph key={itemKey} {...commonChartProps} />;

      case "paymentDistributionChart":
        return <DashboardPaymentDistributionGraph key={itemKey} {...commonChartProps} />;

      case "recentTransactionList":
        return <DashboardRecentTransactions key={itemKey} {...tableProps} tenantType={tenantType} />;

      // case "topPerformingMerchantList":
      //   return <DashboardTopTenants key={itemKey} {...tableProps} tenantType={tenantType} />;

      case "corporationFeeChart":
        return (
          <CorporationFeeGraph
            key={itemKey}
            selectedMerchant={selectedMerchant}
            startDate={startDate}
            endDate={endDate}
            showButton={isEditMode}
            onRemove={handleRemove}
            onShowSnackbar={handleShowSnackbar}
          />
        );

      default:
        return null;
    }
  }, [
    selectedMerchant,
    selectedWebsite,
    selectedTimeRange,
    startDate,
    endDate,
    layoutSlideUpdateIsOpen,
    isEditMode,
    globalKpiData,
    hasTransactionPermission,
    hasWithdrawalPermission,
    handleRemove,
    handleShowSnackbar,
  ]);

  // Check if everything is fully loaded
  const isFullyLoaded = !layoutLoading && kpiDataLoaded;

  return (
    <>
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
      {floatMenu ? <div
        className="backdrop"
        style={{
          cursor: layoutSlideUpdateIsOpen ? 'default' : 'pointer'
        }}
        onClick={() => {
          // Don't close customize sidebar on backdrop click - user should use Cancel button
          if (layoutSlideUpdateIsOpen) {
            // Do nothing - user must use Cancel button in customize sidebar
            return;
          }

          // Close float menu only if customize sidebar is not open
          setFloatMenu(false);
        }}
      ></div> : null}

      {showButton ? (
        <div className={`dash-float-box ${floatMenu ? "active" : ""}`}>
          <div
            className={`btn-float ${floatMenu ? "active" : ""}`}
            onClick={handleFloatMenu}
            style={{ cursor: 'pointer' }}
          >
            <MySettingsIcon
              className={`settings-icon ${floatMenu || layoutSlideUpdateIsOpen ? 'rotating' : ''}`}
              sx={{ fontSize: 30 }}
            />
          </div>
          <div className="float-menu-box" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="float-menu-header">
              <h3 className="float-menu-header-title">Dashboard Layout</h3>
              <p className="float-menu-header-subtitle">Customize your dashboard view</p>
            </div>

            {/* Layout Selection Section */}
            <div className="float-menu-section">
              <label className="float-menu-section-title">Select Layout</label>
              <Select
                labelId="layout-select-label"
                id="layout-select"
                value={layout}
                onChange={handleLayoutChange}
                fullWidth
                size="small"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {/* Always show Default option */}
                <MenuItem value="default">Default</MenuItem>

                {/* Show all dynamic layouts */}
                {getDataForDashboard?.map((layoutItem, index) => (
                  <MenuItem key={index} value={layoutItem.id}>
                    {layoutItem.layoutName}
                  </MenuItem>
                ))}

                {/* Only show Create Layout option when in create mode */}
                {layout === "create-layout" && (
                  <MenuItem value="create-layout">Create Layout</MenuItem>
                )}
              </Select>
            </div>

            {/* Action Buttons Section */}
            {(layout !== "default" && layout !== "create-layout") && (
              <div className="float-menu-section">
                {((layout !== "default" || selectedLayout !== "") && layout !== "create-layout" && selectedLayout !== layout) ? (
                  <div className="float-menu-button-grid">
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleCustomizebtn}
                    >
                      CUSTOMIZE
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleLayoutSelectionChange}
                      disabled={isLayoutSelected}
                    >
                      {isLayoutSelected ? "Selected" : "Select Layout"}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onClick={handleCustomizebtn}
                  >
                    CUSTOMIZE
                  </Button>
                )}
              </div>
            )}

            {layout === "create-layout" && (
              <div className="float-menu-section">
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onClick={() => setLayoutSlideIsOpen(true)}
                >
                  Open Menu
                </Button>
              </div>
            )}

            {(layout !== "default" || selectedLayout !== "") &&
              layout !== "create-layout" &&
              selectedLayout !== layout &&
              layout === "default" && (
                <div className="float-menu-section">
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleLayoutSelectionChange}
                    disabled={isLayoutSelected}
                  >
                    {isLayoutSelected ? "Selected" : "Select Layout"}
                  </Button>
                </div>
              )}

            {/* Divider */}
            <div className="float-menu-divider"></div>

            {/* Quick Actions */}
            <div className="float-menu-section">
              <label className="float-menu-section-title">Quick Actions</label>
              <div className="float-menu-button-grid">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setIsEditMode(false); // Exit edit mode
                    setLayoutSlideIsOpen(true);
                    setLayout("create-layout");
                  }}
                >
                  Create New
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={layout === "create-layout"}
                  onClick={() => {
                    setIsEditMode(false); // Exit edit mode
                    setLayoutSlideUpdateIsOpen(false); // Close edit sidebar if open
                    setLayout("default");
                    setGetValues(
                      updateLayoutConfig(defaultLayout, { static: true, isResizable: false })
                    );
                  }}
                >
                  Default
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <DashboardLayoutSidebar
        layoutSlideIsOpen={layoutSlideIsOpen}
        setLayoutSlideIsOpen={setLayoutSlideIsOpen}
        defaultLayout={defaultLayout}
        getValues={getValues}
        setGetValues={setGetValues}
        handleAddLayout={handleAddLayout}
        setLayout={setLayout}
      />
      <DashboardLayoutSidebarForCustomise
        layoutSlideUpdateIsOpen={layoutSlideUpdateIsOpen}
        setLayoutSlideUpdateIsOpen={setLayoutSlideUpdateIsOpen}
        defaultLayout={defaultLayout}
        getValues={getValues}
        setGetValues={setGetValues}
        handleUpdateLayout={handleUpdateLayout}
        handleDeleteLayout={handleDeleteLayout}
        handleCancelLayoutUpdate={handleCancelLayoutUpdate}
        getDataForDashboard={getDataForDashboard}
        layout={layout}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        setFloatMenu={setFloatMenu}
      />

      <div id="test" className="py-5" ref={containerRef} style={{ width: "100%", position: "relative" }}>
        {/* Edit Mode Indicator */}
        {isEditMode && (
          <div style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#ff9800",
            color: "white",
            padding: "8px 16px",
            borderRadius: "20px",
            fontSize: "14px",
            fontWeight: "600",
            zIndex: 1000,
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
          }}>
            🎨 Edit Mode Active - Drag and resize components
          </div>
        )}

        {/* Header with Global Filters */}
        <div className="d-flex justify-content-between align-items-center mt-5 mb-0 mx-10">
          <h1 className="header-title mb-5">Dashboard</h1>
          <div className="filter-section mb-5 gap-1">
            <FormControl sx={{ m: 1, minWidth: 200 }} className="bg-white" size="small">
              <Select
                labelId="time-range-select-label"
                displayEmpty
                id="time-range-select"
                value={selectedTimeRange}
                onChange={handleGlobalTimeRangeChange}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {TIME_RANGES.map((range) => (
                  <MenuItem key={range.value} value={range.value}>
                    {range.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedTimeRange === "custom" && (
              <Box
                sx={{
                  display: "inline-flex",
                  ml: 2,
                  position: "relative",
                  alignItems: "center",
                }}
              >
                <DateRangePicker
                  selectedRange={selectedDateRange}
                  onRangeChange={(range) => selectCustomDate(range)}
                  maxDate={new Date()}
                  placeholder="Select date range"
                />
              </Box>
            )}

            <FormControl sx={{ m: 1, minWidth: 180 }} size="small">
              <Select
                value={selectedPortal}
                onChange={handlePortalChange}
                displayEmpty
                className="bg-white"
              >
                {PORTAL_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
              <Select
                labelId="merchant-select-label"
                className="bg-white"
                id="merchant-select"
                value={selectedMerchant ? `${selectedMerchant.firstName} ${selectedMerchant.lastName}` : "All Merchant"}
                onChange={handleMerchantChange}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: { maxHeight: 250 }
                  },
                  MenuListProps: {
                    style: {
                      maxHeight: 250,
                      overflowY: "auto",
                    }
                  }
                }}
                disabled={
                  merchantOptions?.length === 0 ||
                  (merchantOptions?.length === 1 && merchantOptions[0] === "All Merchant")
                }
              >
                {merchantOptions.map((merchant, index) => {
                  if (merchant === "All Merchant") {
                    return (
                      <MenuItem key={index} value={merchant}>
                        {"All Tenants"}
                      </MenuItem>
                    );
                  }

                  const matchedMerchant = merchantData.find(
                    (m) => `${m.firstName || ""} ${m.lastName || ""}`.trim() === merchant
                  );

                  const displayText = matchedMerchant
                    ? `${matchedMerchant.firstName || ""} ${matchedMerchant.lastName || ""}`.trim()
                    : merchant;

                  return (
                    <MenuItem key={index} value={merchant}>
                      {displayText}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <IconButton
              onClick={handleResetFilters}
              disabled={!enableFilterButton}
              sx={{
                bgcolor: "#fff",
                "&:hover": { bgcolor: "#fff" },
                ml: 2,
              }}
            >
              <MyRestartAltRoundedIcon sx={{ width: "25px", height: "25px" }} />
            </IconButton>
          </div>
        </div>

        {!isFullyLoaded ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "80vh",
            }}
          >
            <CircularProgress />
          </Box>
        ) : showButton ? (
          <div>
            <GridLayout
              className="layout"
              layout={dynamicLayout}
              cols={12}
              rowHeight={15}
              margin={[25, 25]}
              width={containerRef.current?.offsetWidth}
              dragOutOfBounds={false}
              isDraggable={isEditMode} // Only allow dragging in edit mode
              isResizable={isEditMode} // Only allow resizing in edit mode
              onLayoutChange={(layout) => {
                // Only update layout if in edit mode
                if (isEditMode) {
                  setGetValues(layout);
                }
              }}
              draggableCancel=".close-icon"
            >

              {dynamicLayout.map((item) => (              // ← Updated
                <div key={item.i}>{renderComponent(item.i)}</div>
              ))}
            </GridLayout>
          </div>
        ) : (
          <div>
            {dynamicLayout.map((item) => (           // ← Updated
              <div key={item.i}>{renderComponent(item.i)}</div>
            ))}
          </div>
        )}

      </div>
    </>
  );
};

export default DashboardSection;
